// src/components/ui/DatePicker.tsx
// System Design Glassmorphic Single Date Picker matching Dualon EdTech Design System.
import { useState, useRef, useEffect, useMemo, useLayoutEffect } from 'react'
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, X, ChevronDown, Check } from 'lucide-react'

export interface DatePickerProps {
  value: string // 'YYYY-MM-DD' or ISO string
  onChange: (value: string) => void
  placeholder?: string | undefined
  disabled?: boolean | undefined
  className?: string | undefined
  buttonClassName?: string | undefined
  error?: string | boolean | undefined
  disablePastDates?: boolean | undefined
  minDate?: string | undefined // 'YYYY-MM-DD'
  maxDate?: string | undefined // 'YYYY-MM-DD'
  placement?: ('auto' | 'top' | 'bottom') | undefined
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
]
const DAYS_OF_WEEK = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']

function parseDate(str: string): Date | null {
  if (!str) return null
  if (str.includes('T')) {
    const d = new Date(str)
    return isNaN(d.getTime()) ? null : d
  }
  const parts = str.split('-')
  if (parts.length === 3) {
    const y = Number(parts[0])
    const m = Number(parts[1]) - 1
    const d = Number(parts[2])
    const date = new Date(y, m, d)
    return isNaN(date.getTime()) ? null : date
  }
  const d = new Date(str)
  return isNaN(d.getTime()) ? null : d
}

function formatDateToYMD(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

export default function DatePicker({
  value,
  onChange,
  placeholder = 'Select date...',
  disabled = false,
  className = '',
  buttonClassName = '',
  error = false,
  disablePastDates = true,
  minDate,
  maxDate,
  placement = 'auto',
}: DatePickerProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [computedPlacement, setComputedPlacement] = useState<'top' | 'bottom'>('bottom')
  const containerRef = useRef<HTMLDivElement>(null)

  const todayStart = useMemo(() => {
    const d = new Date()
    d.setHours(0, 0, 0, 0)
    return d
  }, [])

  const selectedDate = useMemo(() => parseDate(value), [value])

  const [viewYear, setViewYear] = useState<number>(() =>
    selectedDate ? selectedDate.getFullYear() : new Date().getFullYear()
  )
  const [viewMonth, setViewMonth] = useState<number>(() =>
    selectedDate ? selectedDate.getMonth() : new Date().getMonth()
  )

  useEffect(() => {
    if (selectedDate) {
      setViewYear(selectedDate.getFullYear())
      setViewMonth(selectedDate.getMonth())
    }
  }, [selectedDate])

  // Custom Month and Year Dropdown state
  const [monthDropdownOpen, setMonthDropdownOpen] = useState(false)
  const [yearDropdownOpen, setYearDropdownOpen] = useState(false)
  const monthDropdownRef = useRef<HTMLDivElement>(null)
  const yearDropdownRef = useRef<HTMLDivElement>(null)
  const yearActiveButtonRef = useRef<HTMLButtonElement>(null)

  const yearList = useMemo(() => {
    const currentYear = new Date().getFullYear()
    return Array.from({ length: 70 }, (_, i) => currentYear + 2 - i)
  }, [])

  // Auto-scroll active year into view
  useEffect(() => {
    if (yearDropdownOpen && yearActiveButtonRef.current) {
      yearActiveButtonRef.current.scrollIntoView({ block: 'nearest' })
    }
  }, [yearDropdownOpen])

  // Close Month/Year popups when clicking outside them
  useEffect(() => {
    if (!monthDropdownOpen && !yearDropdownOpen) return
    function handleHeaderDropdownOutside(e: MouseEvent) {
      if (monthDropdownRef.current && !monthDropdownRef.current.contains(e.target as Node)) {
        setMonthDropdownOpen(false)
      }
      if (yearDropdownRef.current && !yearDropdownRef.current.contains(e.target as Node)) {
        setYearDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleHeaderDropdownOutside)
    return () => document.removeEventListener('mousedown', handleHeaderDropdownOutside)
  }, [monthDropdownOpen, yearDropdownOpen])

  // Compute smart top/bottom placement based on available viewport space
  useLayoutEffect(() => {
    if (!isOpen) return

    const updatePlacement = () => {
      if (placement === 'top') {
        setComputedPlacement('top')
        return
      }
      if (placement === 'bottom') {
        setComputedPlacement('bottom')
        return
      }

      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect()
        const viewportHeight = window.innerHeight
        const spaceBelow = viewportHeight - rect.bottom
        const spaceAbove = rect.top

        // If space below is less than 300px and space above is sufficient, open upwards
        if (spaceBelow < 300 && spaceAbove > 240) {
          setComputedPlacement('top')
        } else {
          setComputedPlacement('bottom')
        }
      }
    }

    updatePlacement()
    window.addEventListener('resize', updatePlacement)
    window.addEventListener('scroll', updatePlacement, true)
    return () => {
      window.removeEventListener('resize', updatePlacement)
      window.removeEventListener('scroll', updatePlacement, true)
    }
  }, [isOpen, placement])

  // Handle outside clicks to close the popover
  useEffect(() => {
    if (disabled) {
      setIsOpen(false)
      return
    }
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [disabled])

  // Check if previous month navigation should be disabled when past dates are disabled
  const isPrevMonthDisabled = useMemo(() => {
    if (!disablePastDates && !minDate) return false
    const currentViewStart = new Date(viewYear, viewMonth, 1)
    const minAllowedStart = minDate
      ? new Date(parseDate(minDate)?.getFullYear() || todayStart.getFullYear(), parseDate(minDate)?.getMonth() || todayStart.getMonth(), 1)
      : new Date(todayStart.getFullYear(), todayStart.getMonth(), 1)
    return currentViewStart.getTime() <= minAllowedStart.getTime()
  }, [disablePastDates, minDate, viewYear, viewMonth, todayStart])

  const handlePrevMonth = () => {
    if (isPrevMonthDisabled) return
    if (viewMonth === 0) {
      setViewMonth(11)
      setViewYear((y) => y - 1)
    } else {
      setViewMonth((m) => m - 1)
    }
  }

  const handleNextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0)
      setViewYear((y) => y + 1)
    } else {
      setViewMonth((m) => m + 1)
    }
  }

  const gridData = useMemo(() => {
    const firstDayIndex = new Date(viewYear, viewMonth, 1).getDay()
    const daysInCurrentMonth = new Date(viewYear, viewMonth + 1, 0).getDate()
    const daysInPrevMonth = new Date(viewYear, viewMonth, 0).getDate()

    const cells: Array<{
      day: number
      monthOffset: number
      date: Date
      dateStr: string
    }> = []

    // Previous month overflow days
    for (let i = firstDayIndex - 1; i >= 0; i--) {
      const dayNum = daysInPrevMonth - i
      const pMonth = viewMonth === 0 ? 11 : viewMonth - 1
      const pYear = viewMonth === 0 ? viewYear - 1 : viewYear
      const cellDate = new Date(pYear, pMonth, dayNum)
      cells.push({
        day: dayNum,
        monthOffset: -1,
        date: cellDate,
        dateStr: formatDateToYMD(cellDate),
      })
    }

    // Current month days
    for (let d = 1; d <= daysInCurrentMonth; d++) {
      const cellDate = new Date(viewYear, viewMonth, d)
      cells.push({
        day: d,
        monthOffset: 0,
        date: cellDate,
        dateStr: formatDateToYMD(cellDate),
      })
    }

    // Next month overflow days
    const totalCells = cells.length > 35 ? 42 : 35
    const remaining = totalCells - cells.length
    for (let d = 1; d <= remaining; d++) {
      const nMonth = viewMonth === 11 ? 0 : viewMonth + 1
      const nYear = viewMonth === 11 ? viewYear + 1 : viewYear
      const cellDate = new Date(nYear, nMonth, d)
      cells.push({
        day: d,
        monthOffset: 1,
        date: cellDate,
        dateStr: formatDateToYMD(cellDate),
      })
    }

    return cells
  }, [viewYear, viewMonth])

  const handleSelectDate = (date: Date, dateStr: string) => {
    const dayStart = new Date(date.getFullYear(), date.getMonth(), date.getDate())
    if (disablePastDates && dayStart.getTime() < todayStart.getTime()) {
      return
    }
    if (minDate && dateStr < minDate) return
    if (maxDate && dateStr > maxDate) return

    onChange(dateStr)
    setIsOpen(false)
  }

  const handleSelectToday = () => {
    const todayStr = formatDateToYMD(new Date())
    onChange(todayStr)
    setIsOpen(false)
  }

  const handleClear = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation()
    onChange('')
    setIsOpen(false)
  }

  const formattedDisplay = useMemo(() => {
    if (!selectedDate) return ''
    return selectedDate.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }, [selectedDate])

  const todayStr = formatDateToYMD(new Date())

  return (
    <div className={`relative w-full ${className}`} ref={containerRef}>
      {/* Trigger Button */}
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setIsOpen((p) => !p)}
        className={`
          w-full flex items-center justify-between px-3 py-2 sm:py-2.5 rounded-xl border text-xs sm:text-sm text-left
          bg-[var(--field)] text-[var(--txt)] outline-none focus:outline-none focus-visible:outline-none
          transition-all duration-200 font-semibold select-none
          ${
            error
              ? 'border-red-500 focus:ring-2 focus:ring-red-500/20'
              : 'border-[var(--field-brd)] focus:ring-2 focus:ring-[var(--mint-500)]/20 focus:border-[var(--mint-500)] hover:border-[var(--txt-3)]'
          }
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
          ${buttonClassName}
        `}
      >
        <span className="flex items-center gap-2 truncate">
          <CalendarIcon size={16} className="text-[var(--mint-500)] shrink-0" />
          <span className={formattedDisplay ? 'text-[var(--txt)] font-bold' : 'text-[var(--txt-3)] font-medium'}>
            {formattedDisplay || placeholder}
          </span>
        </span>

        {value ? (
          <X
            size={15}
            className="text-[var(--txt-3)] hover:text-[var(--rose)] transition-colors shrink-0 ml-1 cursor-pointer"
            onClick={handleClear}
          />
        ) : (
          <CalendarIcon size={15} className="text-[var(--txt-3)] shrink-0 ml-1" />
        )}
      </button>

      {/* Popover Calendar Dropdown */}
      {isOpen && !disabled && (
        <div
          className={`
            absolute left-0 w-full min-w-[280px] bg-[var(--bg-2)] border border-[var(--glass-brd)]
            rounded-2xl shadow-2xl z-[10020] p-3 flex flex-col gap-2.5 backdrop-blur-md animate-fadeIn
            ${computedPlacement === 'top' ? 'bottom-full mb-2' : 'top-full mt-2'}
          `}
        >
          {/* Header (Month, Year & Prev/Next buttons) */}
          <div className="flex items-center justify-between pb-0.5">
            <div className="flex items-center gap-1">
              {/* Custom Month Dropdown */}
              <div className="relative" ref={monthDropdownRef}>
                <button
                  type="button"
                  onClick={() => {
                    setMonthDropdownOpen((p) => !p)
                    setYearDropdownOpen(false)
                  }}
                  className={`
                    flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-extrabold tracking-wide
                    transition-all cursor-pointer font-[family-name:var(--fs-ui)]
                    ${
                      monthDropdownOpen
                        ? 'bg-[var(--mint-500)]/15 text-[var(--mint-500)]'
                        : 'text-[var(--txt)] hover:bg-[var(--glass-2)] hover:text-[var(--mint-500)]'
                    }
                  `}
                >
                  <span>{MONTH_NAMES[viewMonth]}</span>
                  <ChevronDown
                    size={12}
                    className={`transition-transform duration-200 shrink-0 ${
                      monthDropdownOpen ? 'rotate-180 text-[var(--mint-500)]' : 'text-[var(--txt-3)]'
                    }`}
                  />
                </button>

                {monthDropdownOpen && (
                  <div className="absolute left-0 top-full mt-1.5 w-36 bg-[var(--bg-1)] border border-[var(--glass-brd)] rounded-xl shadow-2xl z-[10030] max-h-48 overflow-y-auto scrollbar-none backdrop-blur-md p-1 space-y-0.5 animate-in fade-in zoom-in-95 duration-100">
                    {MONTH_NAMES.map((name, idx) => {
                      const isCurrent = idx === viewMonth
                      return (
                        <button
                          key={name}
                          type="button"
                          onClick={() => {
                            setViewMonth(idx)
                            setMonthDropdownOpen(false)
                          }}
                          className={`
                            w-full text-left px-2.5 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer flex items-center justify-between
                            ${
                              isCurrent
                                ? 'bg-[var(--mint-500)]/15 text-[var(--mint-500)] font-extrabold'
                                : 'text-[var(--txt)] hover:bg-[var(--glass-brd)]/50'
                            }
                          `}
                        >
                          <span>{name}</span>
                          {isCurrent && <Check size={13} className="text-[var(--mint-500)] stroke-[2.5]" />}
                        </button>
                      )
                    })}
                  </div>
                )}
              </div>

              {/* Custom Year Dropdown */}
              <div className="relative" ref={yearDropdownRef}>
                <button
                  type="button"
                  onClick={() => {
                    setYearDropdownOpen((p) => !p)
                    setMonthDropdownOpen(false)
                  }}
                  className={`
                    flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-mono font-bold tracking-wide
                    transition-all cursor-pointer
                    ${
                      yearDropdownOpen
                        ? 'bg-[var(--mint-500)]/15 text-[var(--mint-500)]'
                        : 'text-[var(--txt-3)] hover:bg-[var(--glass-2)] hover:text-[var(--txt)]'
                    }
                  `}
                >
                  <span>{viewYear}</span>
                  <ChevronDown
                    size={12}
                    className={`transition-transform duration-200 shrink-0 ${
                      yearDropdownOpen ? 'rotate-180 text-[var(--mint-500)]' : 'text-[var(--txt-3)]'
                    }`}
                  />
                </button>

                {yearDropdownOpen && (
                  <div className="absolute left-0 top-full mt-1.5 w-28 bg-[var(--bg-1)] border border-[var(--glass-brd)] rounded-xl shadow-2xl z-[10030] max-h-48 overflow-y-auto scrollbar-none backdrop-blur-md p-1 space-y-0.5 animate-in fade-in zoom-in-95 duration-100">
                    {yearList.map((y) => {
                      const isCurrent = y === viewYear
                      return (
                        <button
                          key={y}
                          ref={isCurrent ? yearActiveButtonRef : undefined}
                          type="button"
                          onClick={() => {
                            setViewYear(y)
                            setYearDropdownOpen(false)
                          }}
                          className={`
                            w-full text-left px-2.5 py-1.5 rounded-lg text-xs font-mono font-bold transition-all cursor-pointer flex items-center justify-between
                            ${
                              isCurrent
                                ? 'bg-[var(--mint-500)]/15 text-[var(--mint-500)] font-extrabold'
                                : 'text-[var(--txt)] hover:bg-[var(--glass-brd)]/50'
                            }
                          `}
                        >
                          <span>{y}</span>
                          {isCurrent && <Check size={13} className="text-[var(--mint-500)] stroke-[2.5]" />}
                        </button>
                      )
                    })}
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                disabled={isPrevMonthDisabled}
                onClick={handlePrevMonth}
                className={`p-1 rounded-lg transition-all ${
                  isPrevMonthDisabled
                    ? 'opacity-25 cursor-not-allowed text-[var(--txt-3)]'
                    : 'hover:bg-[var(--glass-2)] text-[var(--txt-2)] hover:text-[var(--txt)] cursor-pointer active:scale-95'
                }`}
                title="Previous Month"
              >
                <ChevronLeft size={14} />
              </button>
              <button
                type="button"
                onClick={handleNextMonth}
                className="p-1 rounded-lg hover:bg-[var(--glass-2)] text-[var(--txt-2)] hover:text-[var(--txt)] transition-all cursor-pointer active:scale-95"
                title="Next Month"
              >
                <ChevronRight size={14} />
              </button>
            </div>
          </div>

          {/* Days of Week Header */}
          <div className="grid grid-cols-7 gap-1 text-center select-none border-b border-[var(--glass-brd)] pb-1">
            {DAYS_OF_WEEK.map((d) => (
              <span key={d} className="text-[9.5px] font-extrabold text-[var(--txt-3)] uppercase tracking-wider">
                {d}
              </span>
            ))}
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-1 justify-items-center">
            {gridData.map((cell, idx) => {
              const isSelected = selectedDate && cell.dateStr === formatDateToYMD(selectedDate)
              const isCurrentToday = cell.dateStr === todayStr
              const isOverflow = cell.monthOffset !== 0

              const dayStart = new Date(cell.date.getFullYear(), cell.date.getMonth(), cell.date.getDate())
              const isPast = disablePastDates && dayStart.getTime() < todayStart.getTime()

              let isOutOfRange = false
              if (minDate && cell.dateStr < minDate) isOutOfRange = true
              if (maxDate && cell.dateStr > maxDate) isOutOfRange = true

              const isDisabled = isPast || isOutOfRange

              return (
                <button
                  key={`${cell.dateStr}-${idx}`}
                  type="button"
                  disabled={isDisabled}
                  onClick={() => !isDisabled && handleSelectDate(cell.date, cell.dateStr)}
                  className={`
                    w-8 h-8 rounded-full text-xs font-semibold flex items-center justify-center transition-all duration-150 select-none
                    ${
                      isSelected
                        ? 'bg-[var(--mint-500)] text-[#06231a] font-black shadow-[0_2px_8px_rgba(51,203,152,0.4)] scale-105 cursor-pointer z-10'
                        : isDisabled
                        ? 'text-[var(--txt-3)]/30 opacity-35 line-through cursor-not-allowed bg-transparent select-none'
                        : isCurrentToday
                        ? 'border border-[var(--mint-500)] text-[var(--mint-500)] font-bold hover:bg-[var(--mint-500)]/10 cursor-pointer active:scale-95'
                        : isOverflow
                        ? 'text-[var(--txt-3)] opacity-35 hover:opacity-75 hover:bg-[var(--glass-2)] cursor-pointer'
                        : 'text-[var(--txt)] hover:bg-[var(--glass-2)] hover:text-[var(--txt)] cursor-pointer active:scale-95'
                    }
                  `}
                >
                  <span>{cell.day}</span>
                </button>
              )
            })}
          </div>

          {/* Quick Actions & Validation Notice Footer */}
          <div className="flex items-center justify-between pt-2 border-t border-[var(--glass-brd)] select-none">
            <button
              type="button"
              onClick={() => handleClear()}
              className="text-[11px] font-semibold text-[var(--txt-3)] hover:text-[var(--rose)] transition-colors cursor-pointer px-1 py-0.5 rounded-md hover:bg-[var(--glass)]"
            >
              Clear
            </button>

            <button
              type="button"
              onClick={handleSelectToday}
              className="flex items-center gap-1 text-[11px] font-extrabold text-[var(--mint-500)] hover:text-[var(--mint-400)] transition-colors cursor-pointer px-2 py-0.5 rounded-lg bg-[var(--mint-500)]/10 hover:bg-[var(--mint-500)]/18 border border-[var(--mint-500)]/20"
            >
              <span>Today</span>
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
